import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { useUi, cx } from "../ui";
import { formatCollectionHours, formatMoney } from "../helpers";
import { useInstallation } from "../../../components/InstallationContext";
import LoadingIndicator from "../../../components/LoadingIndicator";
import {
  formatWhen,
  isCollectable,
  offsetIso,
  PICKS_A_DAY,
  PICKUP_OFFSETS,
  pickupSlots,
} from "../pickupSlots";
import usePickupCharge from "../dashboard/usePickupCharge";
import usePickupCollections from "../dashboard/usePickupCollections";
import useVenueClock from "../dashboard/useVenueClock";
import useSettleBalance from "../dashboard/useSettleBalance";
import useActiveBlackoutPeriods from "../blackouts/useActiveBlackoutPeriods";
import { isBlackoutInstant } from "../blackouts/blackoutHelpers";
import AddCardModal from "./AddCardModal";
import ModalCard from "./ModalCard";
import PickupTimePicker from "./PickupTimePicker";
import Plate from "./Plate";

const useStyles = makeStyles({
  head: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
    fontSize: 14,
    color: T.fg,
  },
  box: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    fontSize: 13,
    marginBottom: 12,
  },
  line: { display: "flex", justifyContent: "space-between", gap: 12 },
  total: {
    fontWeight: 700,
    borderTop: `1px solid ${T.borderSoft}`,
    paddingTop: 6,
  },
  notice: {
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    background: T.surface,
    color: T.mutedFg,
    marginBottom: 12,
  },
  warn: { background: T.surface, color: T.fg },
  noticeTitle: { fontWeight: 600, color: T.fg },
  payment: {
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontSize: 13,
  },
  payTitle: { fontWeight: 600 },
  meta: { fontSize: 12, color: T.mutedFg },
  error: { color: T.danger, fontSize: 12 },
  small: { padding: "8px 12px", fontSize: 13 },
  loading: { display: "flex", justifyContent: "center", padding: "24px 0" },
});

/*
 * What the desk confirms before a car joins the pickup queue: the vehicle, what
 * is owed, and one button that settles it and lodges the pickup.
 */
export default function PickupRequestModal({ row, onClose, onLodged }) {
  const s = useStyles();
  const ui = useUi();
  const { installation, facility } = useInstallation() ?? {};
  const charge = usePickupCharge(row.bookingId);
  const collections = usePickupCollections(row.bookingId);
  const settlement = useSettleBalance(charge, row.bookingId);
  const blackoutPeriods = useActiveBlackoutPeriods(installation?.id);
  const [submitting, setSubmitting] = useState(false);
  const [offset, setOffset] = useState("NOW");
  const [chosenTime, setChosenTime] = useState(null);

  const rescheduling =
    row.status === "REQUESTED" || row.status === "RUNNER_ASSIGNED";
  const payable =
    !rescheduling && !charge.hotelInvoices && charge.outstanding > 0;
  const timezone = installation?.timeZoneName;
  const hours = formatCollectionHours(collections.windows, timezone);

  /* Kept running: the desk can sit on this modal, and the times it offers -
     and the one it submits - have to move with the clock rather than with
     whenever it happened to open. */
  const now = useVenueClock(collections.serverTime);

  /* What bounds a pickup: the hours somebody is rostered for, and the instant
     the card behind this booking expires. */
  const limits = useMemo(
    () => ({
      windows: collections.windows,
      expiresAt: collections.expiresAt,
      blackouts: blackoutPeriods.periods,
      timezone,
    }),
    [collections.windows, collections.expiresAt, blackoutPeriods.periods, timezone],
  );

  /* A time picked on one day means nothing on the other, so changing chip
     clears it rather than submitting yesterday's answer. */
  const chooseOffset = (next) => {
    setOffset(next);
    setChosenTime(null);
  };

  const slots = useMemo(
    () =>
      pickupSlots({
        now,
        timezone,
        day: offset === "TOMORROW" ? "TOMORROW" : "TODAY",
        ...limits,
      }),
    [now, timezone, offset, limits],
  );

  /* A day with nothing bookable left on it opens a grid of struck-through
     times, so the chip that would open it is dropped: late in the evening for
     today, and past the card's expiry for tomorrow. */
  const bookableDays = useMemo(
    () => ({
      TODAY: pickupSlots({ now, timezone, ...limits }).some(
        (slot) => slot.available,
      ),
      TOMORROW: pickupSlots({
        now,
        timezone,
        day: "TOMORROW",
        ...limits,
      }).some((slot) => slot.available),
    }),
    [now, timezone, limits],
  );

  /* The fixed chips name a time of their own, so they are blocked out the same
     way a slot is rather than leading to a refusal at the button. */
  const unavailableOffsets = useMemo(
    () =>
      PICKUP_OFFSETS.filter(
        ({ minutes }) =>
          minutes !== null &&
          !isCollectable(offsetIso(now, timezone, minutes), limits),
      ).map(({ key }) => key),
    [now, timezone, limits],
  );

  const blackoutNow = useMemo(
    () =>
      blackoutPeriods.periods.some((period) =>
        isBlackoutInstant(period, offsetIso(now, timezone, 0), timezone),
      ),
    [blackoutPeriods.periods, now, timezone],
  );

  /* A time that has since passed, or that the roster no longer covers, drops
     out of the grid - and out of the selection with it, rather than staying on
     the button for the desk to submit. */
  useEffect(() => {
    if (!chosenTime) return;
    if (slots.some((slot) => slot.iso === chosenTime && slot.available)) return;

    setChosenTime(null);
  }, [slots, chosenTime]);

  /* The chip the desk left selected, resolved to the one instant the server
   * books against the car. Null means "now", which books nothing. */
  const scheduledAt = useMemo(() => {
    if (offset === "NOW") return null;
    if (PICKS_A_DAY.includes(offset)) return chosenTime;

    const minutes =
      PICKUP_OFFSETS.find((item) => item.key === offset)?.minutes ?? null;

    return minutes === null ? null : offsetIso(now, timezone, minutes);
  }, [offset, chosenTime, now, timezone]);

  const scheduling = offset !== "NOW";
  const chosenLabel = formatWhen(scheduledAt, timezone, now);
  /* Picked a day but not yet a time on it. */
  const incomplete = PICKS_A_DAY.includes(offset) && !scheduledAt;
  const selectedOffsetUnavailable =
    !PICKS_A_DAY.includes(offset) && unavailableOffsets.includes(offset);

  /* The price and the shift window both decide what the button says, so it
   * stays a single "Checking…" until both have landed - otherwise the desk
   * sees "Add to pickup queue" flash before it becomes a charge. */
  const loading = charge.loading || collections.loading || blackoutPeriods.loading;

  /* Being between shifts stops a car being called up now; it is exactly what
   * booking a time is for, so it does not stop that. */
  const blocked =
    loading ||
    (!rescheduling && Boolean(charge.error)) ||
    Boolean(blackoutPeriods.error) ||
    charge.paying ||
    submitting ||
    incomplete ||
    (scheduling
      ? !scheduledAt || selectedOffsetUnavailable
      : !collections.open || blackoutNow);

  const action = scheduling
    ? `schedule${chosenLabel ? ` for ${chosenLabel}` : ""}`
    : "add to queue";

  const readyLabel = rescheduling
    ? scheduling
      ? `Move pickup to ${chosenLabel}`
      : "Bring car now"
    : scheduling
    ? `Schedule pickup for ${chosenLabel}`
    : "Add to pickup queue";

  const label = loading
    ? "Checking…"
    : charge.paying
    ? "Charging…"
    : submitting
    ? rescheduling
      ? "Saving…"
      : scheduling
      ? "Scheduling…"
      : "Adding to queue…"
    : incomplete
    ? "Choose a time"
    : payable
    ? `Pay ${formatMoney(charge.outstanding, charge.currency)} & ${action}`
    : readyLabel;

  /*
   * Taking the money and lodging the pickup are one action to the desk. The
   * payment's own result decides whether the second half runs - the charge
   * state has not re-rendered yet when this reads it.
   */
  const submit = async () => {
    if (blocked) return;

    if (payable) {
      if (!charge.hasCardOnFile) {
        settlement.settle();
        return;
      }

      const result = await charge.pay();

      if (result === "needs-card") {
        settlement.openDifferentCard();
        return;
      }

      /* The error is already on the panel; the car stays where it is. */
      if (result !== "paid") return;
    }

    setSubmitting(true);

    try {
      await onLodged(scheduledAt);
    } finally {
      setSubmitting(false);
    }
  };

  /* What this one pickup costs - on the card, or on the hotel's invoice. */
  const requestFee =
    charge.pendingRequestFee > 0
      ? formatMoney(charge.pendingRequestFee, charge.currency)
      : "Included · no extra fee";

  return (
    <>
      <ModalCard
        title={
          rescheduling
            ? "Change pickup time"
            : scheduling
            ? "Schedule pickup"
            : "Request pickup"
        }
        subtitle={[
          row.guestName || "Unknown guest",
          row.guestRoom ? `Room ${row.guestRoom}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        onClose={charge.paying || submitting ? undefined : onClose}
        width={600}
        z={1400}
        footerLeft={
          <button
            type="button"
            className={cx(ui.mutedBtn, s.small)}
            disabled={charge.paying || submitting}
            onClick={onClose}
          >
            Cancel
          </button>
        }
        footerRight={
          <button
            type="button"
            className={cx(ui.primaryBtn, s.small)}
            disabled={blocked}
            onClick={submit}
          >
            {label}
          </button>
        }
      >
        <div className={s.head}>
          <Plate size={14}>{row.plate}</Plate>
          {(row.bay || row.parkedLocation) && (
            <span className={s.meta}>Bay {row.bay ?? row.parkedLocation}</span>
          )}
          {rescheduling && row.requestedAt && (
            <span className={s.meta}>
              Wanted {formatWhen(row.requestedAt, timezone, now) || "now"}
            </span>
          )}
        </div>

        {/* One indicator for the whole modal: the balance and the shift window
            arrive separately, and letting each section pop in on its own reads
            as the modal changing its mind. */}
        {loading && (
          <div
            className={s.loading}
            aria-label="Checking this pickup"
            aria-busy="true"
          >
            <LoadingIndicator />
          </div>
        )}

        {!loading && blackoutPeriods.error && (
          <div className={s.box} role="alert">
            <div>
              Blackout periods could not be checked. The pickup stays blocked
              until they are known.
            </div>
            <div>
              <button
                type="button"
                className={cx(ui.ghostBtn, s.small)}
                onClick={() => blackoutPeriods.retry()}
              >
                Check again
              </button>
            </div>
          </div>
        )}

        {/* No runner is on shift to fetch it now. Booking a time inside the
            hours is still open to the desk, and the picker below says which. */}
        {!loading && !collections.open && (
          <div className={cx(s.notice, s.warn)} role="alert">
            <div className={s.noticeTitle}>Collections are paused right now</div>
            <div className={s.meta}>
              {hours
                ? `Collections run ${hours}. Book a time inside them instead.`
                : "Book a time inside collection hours instead."}
            </div>
          </div>
        )}

        {!loading && collections.open && blackoutNow && (
          <div className={cx(s.notice, s.warn)} role="alert">
            <div className={s.noticeTitle}>Pickups are blacked out right now</div>
            <div className={s.meta}>Choose an available time outside the blackout period.</div>
          </div>
        )}

        {!loading && (
          <PickupTimePicker
            offset={offset}
            onOffset={chooseOffset}
            slots={slots}
            bookableDays={bookableDays}
            unavailableOffsets={unavailableOffsets}
            chosenTime={chosenTime}
            onChooseTime={setChosenTime}
            collectionHours={hours}
            blackoutPeriods={blackoutPeriods.periods}
            timezone={timezone}
            disabled={charge.paying || submitting || Boolean(blackoutPeriods.error)}
          />
        )}

        {loading || rescheduling ? null : charge.error ? (
          <div className={s.box} aria-label="Charges before confirmation">
            <div>
              The balance could not be checked. The pickup stays blocked until
              it is known.
            </div>
            <div>
              <button
                type="button"
                className={cx(ui.ghostBtn, s.small)}
                onClick={charge.retry}
              >
                Check again
              </button>
            </div>
          </div>
        ) : charge.hotelInvoices ? (
          /* Nothing here is collectable, and the stay is not this modal's
             business: what the desk needs is what this one pickup adds to the
             hotel's invoice. */
          <div className={s.box} aria-label="Charges before confirmation">
            <div className={s.line}>
              <span>This request</span>
              <span>{requestFee}</span>
            </div>
            <div className={cx(s.line, s.total)}>
              <span>Invoiced by the hotel</span>
              <span>{requestFee}</span>
            </div>
          </div>
        ) : (
          /* What the desk is about to take, and the two halves it is made of:
             what the stay already owed, and what this pickup adds. */
          <div className={s.box} aria-label="Charges before confirmation">
            <div className={s.line}>
              <span>Outstanding balance</span>
              <span>
                {formatMoney(charge.outstandingBeforePickup, charge.currency)}
              </span>
            </div>

            <div className={s.line}>
              <span>This request</span>
              <span>{requestFee}</span>
            </div>

            <div className={cx(s.line, s.total)}>
              <span>Due before pickup</span>
              <span>{formatMoney(charge.outstanding, charge.currency)}</span>
            </div>
          </div>
        )}

        {!loading && payable && Boolean(charge.payError) && (
          <div className={s.error}>{charge.payError}</div>
        )}
      </ModalCard>

      {settlement.addCardOpen && (
        <AddCardModal
          bookingId={row.bookingId}
          amountDue={charge.outstanding}
          currency={charge.currency}
          country={facility?.city?.country}
          onClose={settlement.closeAddCard}
          onConfirmed={async () => {
            settlement.closeAddCard();
            const result = await charge.pay();
            if (result !== "paid") return;
            setSubmitting(true);
            try {
              await onLodged(scheduledAt);
            } finally {
              setSubmitting(false);
            }
          }}
        />
      )}
    </>
  );
}
